<script>
  import ToggleSwitch from "../../../components/input/ToggleSwitch.svelte";
  import ItemPerPageSelectBox from "../../../components/select/ItemPerPageSelectBox.svelte";
  import {
    saveDispatchingViewConfiguration,
  } from "../../../utils/configurationStorage/views/DispatchingViewConfigurationStorage.js";
  import GeneralSelect from "../../../components/select/GeneralSelect.svelte";
  import { translate } from "../../../i18n/i18next.js";
  import MenuBar from "../../../components/menuBar/MenuBar.svelte";
  import MenuBarItem from "../../../components/menuBar/MenuBarItem.svelte";
  import PageSelector2 from "../../../components/layout/PageSelector2.svelte";
  import DispatchingViewResourceSelectionDialog from "./DispatchingViewResourceSelectionDialog.svelte";
  import { getResourceNameOrCode } from "./utils.js";
  import HelpTooltip from "../../../components/tooltip/HelpTooltip.svelte";
  import LeftMenuDialog from "../../../components/layout/menuDialog/variants/LeftMenuDialog.svelte";
  import MenuDialogSection from "../../../components/layout/menuDialog/MenuDialogSection.svelte";
  import MenuDialogSectionEntryTitle from "../../../components/layout/menuDialog/MenuDialogSectionEntryTitle.svelte";
  import MenuDialogSectionTitle from "../../../components/layout/menuDialog/MenuDialogSectionTitle.svelte";
  
  import { useMyProjectPermission } from "../../../hooks/useMyProjectPermission";
  import { useInvalidateProjectQuery, useProjectQuery } from "../../../hooks/useProject";
  import { updateProjectSettings } from "../../../components/ganttchart/ganttStore/connectProjectAdminSettings";
  import { getFetchErrorHandlerContext } from "../../../hooks/useFetchErrorHandlerContext";

  export let iStyle = 0;
  export let styles = [];
  export let displayCompletedTasks = false;
  export let usingDatePageMode = false;
  export let itemPerPage;
  export let displayUnitTermColumn = false

  export let styleSelectOnChange;
  export let pageModeOnChange;
  export let totalPageNumber = 1;
  export let currentIndex = 0
  export let dateList = [];
  export let projectId
  export let userId
  export let dayStartTime
  export let currentResource = null
  export let currentResourceGroup = null
  export let resources = null;
  export let resourceGroups = null;
  export let isFirstVisit = false;
  export let onMoveToResource
  export let enableResultEdit;

  const {handleNonBlockingFetchErrors} = getFetchErrorHandlerContext()
  const {invalidateProjectQuery} = useInvalidateProjectQuery()
  const {authorize} = useMyProjectPermission(projectId)


  function onChangeSettings(settingName, newValue, callback = null) {
    if (!userId) return

    saveDispatchingViewConfiguration({ [settingName]: newValue }, userId, projectId);
    if (callback) callback();
  }

  function onChangeStyleSettings(event) {
    const newStyleIndex = event.target.value;
    const newStyleName = styles[newStyleIndex];
    saveDispatchingViewConfiguration({ styleName: newStyleName }, userId, projectId);
    iStyle = parseInt(newStyleIndex, 10)
    styleSelectOnChange();
  }

  let resourceSelectionDialog;
  let resourceSelectionDialogOpen = false; // I don't know why but using showModal() here will make the dialog always open if using display:flex for the DialogBase

  $: if (isFirstVisit) {
    resourceSelectionDialogOpen = true; 
  }

  $: selectableResourceGroups = resourceGroups?.filter(rg => rg.groupMembers.find(member => member.code === currentResource?.code)) ?? []

  let menuDialog = null
</script>

{#if resourceSelectionDialogOpen && currentResource}
  <DispatchingViewResourceSelectionDialog
    projectId={projectId}
    bind:dialog={resourceSelectionDialog}
    onClose={()=>resourceSelectionDialogOpen = false}
    {resources}
    {resourceGroups}
    resourceObjectID={currentResource.objectId}
    {userId}
    onMoveToResource={onMoveToResource}
  />
{/if}


<!-- svelte-ignore a11y-click-events-have-key-events -->
<MenuBar onClickDetailedMenuOpen={() => menuDialog?.showModal()}>
  <svelte:fragment slot="settings">
    <!-- vertical separator -->
    <div style="border-left:1px solid #000;height:20px"></div>

    <!-- Display resource name or code -->
    <MenuBarItem>
      <div on:click={()=>resourceSelectionDialogOpen = true} class="resourceCode">
        {getResourceNameOrCode(currentResource)}
        <!-- Override the color of svg -->
        <div class="svg-icon">
        </div>
      </div>
    </MenuBarItem>

    <!-- SettingItem: resource groups -->
    <!-- This setting is only relevant if the current resource belongs to any resource group. -->
    {#if selectableResourceGroups.length > 1}
      <MenuBarItem>
        <span slot="label">
          {translate("asprova.terms.resourceGroup")}
        </span>
        <span style="display: inline-flex; gap: .5rem; align-items: center">
          <GeneralSelect
            options={selectableResourceGroups.map(rg => ({text: getResourceNameOrCode(rg), value: rg.code}))}
            value={currentResourceGroup?.code}
            onChangeEvent={e => {
              const resourceGroupCode = e.target.value;
              const newResourceGroup = selectableResourceGroups.find(rg => rg.code === resourceGroupCode);
              if (!newResourceGroup) return
              currentResourceGroup = newResourceGroup;
              saveDispatchingViewConfiguration({selectedResourceGroupCode: newResourceGroup.code}, userId, projectId)
            }}
          />

        </span>
      </MenuBarItem>
      <MenuBarItem>
        <HelpTooltip title={translate("asprova.terms.resourceGroup")} iconSize="large">
          {translate("frontend:views.dispatchingView.moveInResourceGroup")}
        </HelpTooltip>
      </MenuBarItem>
    {/if}

    <!-- vertical separator -->
    <div style="border-left:1px solid #000;height:20px"></div>

    <!-- SettingItem: styles -->
    <MenuBarItem>
      <svelte:fragment slot="label">{translate("asprova.terms.style")}</svelte:fragment>
      <GeneralSelect
        options={styles.map((style, index) => ({text: style, value: index}))}
        bind:value={iStyle}
        onChangeEvent={onChangeStyleSettings}
      />
    </MenuBarItem>

    <!-- SettingItem: should completed tasks be displayed?  -->
    <MenuBarItem>
      <svelte:fragment slot="label">{translate("frontend:views.dispatchingView.control.displayCompletedTasks")}</svelte:fragment>
      <ToggleSwitch
        bind:on={displayCompletedTasks}
        onChange={(e) => onChangeSettings("displayCompletedTasks", e.target.checked)}
      />
    </MenuBarItem>

    <!-- SettingItem: enable date page mode? -->
    <MenuBarItem>
      <svelte:fragment slot="label">{translate("frontend:views.dispatchingView.control.dateMode")}</svelte:fragment>
      <ToggleSwitch
        bind:on={usingDatePageMode}
        onChange={(e) => onChangeSettings("usingDatePageMode", e.target.checked, pageModeOnChange)}
      />
    </MenuBarItem>

    <!-- SettingItem: set items per page; gets disabled in date page mode. -->
    {#if !usingDatePageMode}
      <MenuBarItem>
        <svelte:fragment slot="label">{translate("frontend:generic.itemsPerPage")}</svelte:fragment>
        <ItemPerPageSelectBox
          choices={[1, 3, 5, 10, 20, 30, 50, 100]}
          bind:value={itemPerPage}
          onChange={(e) => onChangeSettings("itemPerPage", parseInt(e.target.value, 10))}
        />
      </MenuBarItem>
    {/if}
  </svelte:fragment>
  <MenuBarItem slot="pagination">
    {#if usingDatePageMode && dateList && dateList.length > 0}
      <PageSelector2
        totalPageNumber={dateList.length}
        {dateList}
        bind:currentIndex={currentIndex}
        {dayStartTime}
      />
    {:else}
      <PageSelector2
        totalPageNumber={totalPageNumber}
        bind:currentIndex={currentIndex}
      />
    {/if}
  </MenuBarItem>
</MenuBar>
<LeftMenuDialog bind:dialog={menuDialog} onClose={() => menuDialog?.close()}>
  <MenuDialogSection>
    <MenuDialogSectionTitle>
      {translate(
        "frontend:ganttChart.settings.sections.displaySettings.title"
      )}
    </MenuDialogSectionTitle>
    <div class="toggle-switch-container">
      <MenuDialogSectionEntryTitle>
        {translate("frontend:views.dispatchingView.control.displayUnitTermColumn.title")}
        <HelpTooltip title={translate("frontend:views.dispatchingView.control.displayUnitTermColumn.tooltip.title")}>
          {translate("frontend:views.dispatchingView.control.displayUnitTermColumn.tooltip.caption")}
        </HelpTooltip>
      </MenuDialogSectionEntryTitle>
      <div class="toggle-switch">
        <ToggleSwitch
          bind:on={displayUnitTermColumn}
          onChange={(e) => onChangeSettings("displayUnitTermColumn", e.target.checked)}
        />
      </div>
    </div>
  </MenuDialogSection>
  <div style="margin-top:30px"/>
  <MenuDialogSection>
    <MenuDialogSectionTitle>
      {translate(
        "frontend:generic.projectAdminSettings.title"
      )}
      <HelpTooltip title={translate(
        "frontend:generic.projectAdminSettings.title"
      )}>
        {translate("frontend:generic.projectAdminSettings.tooltip")}
      </HelpTooltip>
    </MenuDialogSectionTitle>

    <div class="toggle-switch-container">
      <MenuDialogSectionEntryTitle>
        {translate("generic.editResultShort")} 
      </MenuDialogSectionEntryTitle>
      <div class="toggle-switch">
        <ToggleSwitch
          disabled={!$authorize("ProjectAdmin")}
          on={$enableResultEdit}
          onChange={async (e) => {
            enableResultEdit.set(e.target.checked);
            await updateProjectSettings(
              projectId,
              { isDispatchingViewResultEditEnabled: e.target.checked },
              handleNonBlockingFetchErrors,
              () => invalidateProjectQuery()
            );
          }}
        />
      </div>
    </div>
  </MenuDialogSection>
</LeftMenuDialog>


<style>
  div.resourceCode {
    display: flex;
    align-items: center;
    cursor: pointer;
    color: black;
    height: 100%;
    font-size: x-large;
  }
  div.resourceCode:hover {
    background-color: #e2e0e2;
  }

  .svg-icon {
    mask: url('/assets/rss_down.svg') no-repeat center;
    -webkit-mask: url('/assets/rss_down.svg') no-repeat center;
    background-color: #00468f; 
    width: 24px;
    height: 24px;
  }

  .toggle-switch-container {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .toggle-switch {
    margin-left: auto;
  }
</style>
